/**
 * "Dodaj jako fakturę zakupu": faktura z KSeF -> POST /ksef/otrzymane/<id>/dodaj-fakture {location_id}.
 * Bez kreatora i bez cennika, wiec produkty MUSZA juz byc w systemie (GTIN z XML / nauka kodow SFD / dokladna nazwa).
 * Parser nigdy nie zaklada produktow: przy 400 BRAK_POKRYCIA tabelka brakujacych (kod SFD, nazwa, ilosc, EAN) z err.data,
 * admin dodaje produkty i klika "Spróbuj ponownie". 409 FAKTURA_ISTNIEJE (err.data.istniejaca) -> odnosnik do istniejacej
 * faktury. Sklep domyslnie z dopasowania (faktura w systemie > wiersz kontroli SFD z adresu dostawy).
 * Korekty nie sa dodawane ta droga (backend: 400 ZLY_STATUS) - modal tylko o tym informuje.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ksefOtrzymaneService } from '../../services/ksefOtrzymaneService';
import { formatujKwote, formatujDate, formatujIlosc, komunikatBledu, daneBledu, RODZAJE, jestKorekta, sklepWiersza } from './format';

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const th = { padding: '4px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'top' };

const DodajFaktureModal = ({ wiersz, sklepy, onZamknij, onDodano }) => {
  const sklepZKsef = sklepWiersza(wiersz);
  const listaSklepow = Array.isArray(sklepy) ? sklepy.filter(s => s && s.id !== null && s.id !== undefined) : [];
  // sklep z dopasowania moze nie byc na liscie lokalizacji - dolozyc, zeby domyslny wybor byl mozliwy
  if (sklepZKsef && !listaSklepow.some(s => Number(s.id) === Number(sklepZKsef.id))) {
    listaSklepow.push({ id: sklepZKsef.id, nazwa: sklepZKsef.nazwa || `Sklep #${sklepZKsef.id}` });
  }
  const [locationId, setLocationId] = useState(sklepZKsef ? String(sklepZKsef.id) : '');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState(null);
  const [pokrycie, setPokrycie] = useState(null);     // {brakujace: [{kod_sfd, nazwa, ilosc, ean}], dopasowane, komunikat}
  const [istniejaca, setIstniejaca] = useState(null); // {id, numer, sklep, ...} po 409 FAKTURA_ISTNIEJE
  const [wynik, setWynik] = useState(null);           // {faktura_id, numer, ksef_numer, pozycji, location_id, nauczono}

  const korekta = jestKorekta(wiersz);
  const opisSklepu = sklepZKsef?.zrodlo === 'faktura' ? ' (wg faktury w systemie)' : ' (wg adresu dostawy SFD)';

  const zapisz = async () => {
    if (!locationId) { setBlad('Wybierz sklep, do którego trafi faktura.'); return; }
    setWysylanie(true);
    setBlad(null);
    setIstniejaca(null);
    try {
      const dane = await ksefOtrzymaneService.dodajFakture(wiersz.id, Number(locationId));
      setPokrycie(null);
      setWynik(dane || {});
      toast.success(`Dodano fakturę ${dane?.numer || wiersz.numer_faktury} z KSeF`);
      if (onDodano) onDodano(dane, wiersz);
    } catch (err) {
      const d = daneBledu(err);
      if (err?.errorCode === 'BRAK_POKRYCIA') {
        setPokrycie({ brakujace: Array.isArray(d.brakujace) ? d.brakujace : [], dopasowane: d.dopasowane, komunikat: d.komunikat || d.error || null });
      } else if (err?.errorCode === 'FAKTURA_ISTNIEJE' || err?.errorCode === 'JUZ_W_SYSTEMIE' || err?.status === 409) {
        setIstniejaca(d.istniejaca || {});
        setBlad(d.komunikat || d.error || 'Ta faktura jest już w systemie.');
      } else if (!err?.isAuthError) {
        setBlad(komunikatBledu(err, 'Nie udało się dodać faktury z KSeF.'));
      }
    } finally {
      setWysylanie(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="modal-ksef-dodaj-tytul" data-testid="modal-dodaj-ksef"
        style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '560px', padding: '1.25rem', fontSize: '12px', color: 'var(--text)', maxHeight: '90vh', overflow: 'auto' }}>
        <h5 id="modal-ksef-dodaj-tytul" style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700 }}>
          {korekta ? 'Korekta z KSeF' : 'Dodaj jako fakturę zakupu'}
        </h5>
        <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text)' }}>{wiersz?.numer_faktury}</strong>
          {wiersz?.rodzaj && <> · {RODZAJE[wiersz.rodzaj]?.pelna || wiersz.rodzaj}</>}
          {' · '}{wiersz?.sprzedawca_nazwa || wiersz?.sprzedawca_nip || '—'}
          {' · '}{formatujDate(wiersz?.data_wystawienia)}
          {wiersz?.brutto !== null && wiersz?.brutto !== undefined && <> · {formatujKwote(wiersz.brutto)}</>}
        </p>

        {wynik ? (
          <div data-testid="ksef-dodaj-wynik" style={{ padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--success-light)', color: 'var(--success)', marginBottom: '12px' }}>
            Dodano fakturę <strong>{wynik.numer || wiersz?.numer_faktury}</strong>
            {wynik.pozycji !== undefined && <> ({wynik.pozycji} pozycji)</>}.
            {Number(wynik.nauczono) > 0 && <> Nauczono {wynik.nauczono} {wynik.nauczono === 1 ? 'nowy kod SFD' : 'nowych kodów SFD'}.</>}
            {wynik.faktura_id && (
              <> <Link to={`/purchase-invoices/${wynik.faktura_id}`} style={{ color: 'var(--success)', fontWeight: 600 }}>Otwórz fakturę #{wynik.faktura_id}</Link></>
            )}
          </div>
        ) : korekta ? (
          <div data-testid="ksef-dodaj-korekta" style={{ padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--orange-light)', color: 'var(--orange)', marginBottom: '12px', fontSize: '11px' }}>
            Korekty z KSeF nie są dodawane automatycznie — korekty SFD dodaje zakładka „Kontrola korekt SFD”. Tu tylko podgląd.
          </div>
        ) : (
          <>
            <div data-testid="ksef-dodaj-info" style={{ padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--info-light)', color: 'var(--info)', marginBottom: '10px', fontSize: '11px' }}>
              Pozycje z XML KSeF bez cennika — produkty muszą już być w systemie (po EAN z XML, kodzie SFD z nauki albo dokładnej nazwie). Ceny zakupu z faktury.
            </div>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
              Sklep{' '}
              <select aria-label="Sklep docelowy" data-testid="ksef-dodaj-sklep" value={locationId} disabled={wysylanie}
                onChange={(e) => setLocationId(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', minWidth: '200px' }}>
                <option value="">— wybierz sklep —</option>
                {listaSklepow.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nazwa}{sklepZKsef && Number(s.id) === Number(sklepZKsef.id) ? opisSklepu : ''}</option>
                ))}
              </select>
            </label>

            {pokrycie && (
              <div data-testid="ksef-brak-pokrycia" style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--warning-light)', border: '1px solid var(--warning)' }}>
                <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: '6px' }}>
                  Brak {pokrycie.brakujace.length} {pokrycie.brakujace.length === 1 ? 'produktu' : 'produktów'} w systemie
                  {pokrycie.dopasowane !== undefined && pokrycie.dopasowane !== null && (
                    <span data-testid="ksef-dopasowane" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · dopasowano: {pokrycie.dopasowane}</span>
                  )}
                </div>
                <div style={{ overflow: 'auto', marginBottom: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr><th style={th}>Kod SFD</th><th style={th}>Nazwa na fakturze</th><th style={{ ...th, textAlign: 'right' }}>Ilość</th><th style={th}>EAN</th></tr>
                    </thead>
                    <tbody>
                      {pokrycie.brakujace.map((b, i) => (
                        <tr key={`${b.kod_sfd || i}-${b.ean || ''}`} data-testid="ksef-brakujacy">
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{b.kod_sfd || '—'}</td>
                          <td style={td}>{b.nazwa || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatujIlosc(b.ilosc)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: b.ean ? 'var(--text)' : 'var(--text-muted)' }} title={b.ean ? 'EAN znany, ale produktu nie ma w systemie' : 'EAN nieznany (brak GTIN w XML, nauki i dopasowania po nazwie)'}>{b.ean || 'brak'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ color: 'var(--text)', fontSize: '11px' }}>
                  Dodaj te produkty do systemu (np. przez cennik) i spróbuj ponownie.
                  {pokrycie.komunikat && <span style={{ color: 'var(--text-muted)' }}> ({pokrycie.komunikat})</span>}
                </div>
              </div>
            )}

            {blad && (
              <div data-testid="ksef-dodaj-blad" style={{ marginBottom: '10px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)' }}>
                {blad}
                {istniejaca && (istniejaca.id || istniejaca.numer) && (
                  <span data-testid="ksef-istniejaca">
                    {' '}
                    {istniejaca.id
                      ? <Link to={`/purchase-invoices/${istniejaca.id}`} style={{ color: 'var(--danger)', fontWeight: 600 }}>Otwórz {istniejaca.numer || `fakturę #${istniejaca.id}`}</Link>
                      : <strong>{istniejaca.numer}</strong>}
                    {istniejaca.sklep && <> (sklep: {istniejaca.sklep})</>}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onZamknij} style={btnSecondary} data-testid="ksef-dodaj-zamknij">{wynik ? 'Zamknij' : 'Anuluj'}</button>
          {!wynik && !istniejaca && !korekta && (
            <button type="button" onClick={zapisz} disabled={wysylanie} data-testid="ksef-dodaj-zapisz"
              style={{ ...btnPrimary, opacity: wysylanie ? 0.6 : 1 }}>
              {wysylanie ? 'Dodaję…' : pokrycie ? 'Spróbuj ponownie' : 'Dodaj fakturę'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DodajFaktureModal;
